import type { FilterOptionItem } from '@odo/shared-ui';
import { compactName } from '../components/conversations/assigneeSelectorHelpers.ts';
import { legacyAssigneeLabels } from './legacyAssigneeLabels.ts';

// Stored as-is (URLs, saved views) and only mapped to an API's terms at query time, so a
// shared link with "Me" still means whoever opens it.
export const ASSIGNEE_FILTER_UNASSIGNED = 'Unassigned';
export const ASSIGNEE_FILTER_ME = 'Me';
// The work queue and Monitoring APIs read this as "assignee IS NULL".
export const ASSIGNEE_NULL_SENTINEL = 'null';

export interface AssigneeFilterPerson {
    email: string;
    fullname: string;
}

const isSentinel = (value: string) =>
    value === ASSIGNEE_FILTER_UNASSIGNED || value === ASSIGNEE_FILTER_ME;

// "Unassigned", "Me" when signed in, then everyone else by compact name, valued by email.
export function buildAssigneeFilterOptions(
    people: readonly AssigneeFilterPerson[],
    currentUserEmail?: string
): FilterOptionItem[] {
    const others = people
        .filter((person) => person.email !== currentUserEmail)
        .map((person) => ({ person, label: compactName(person.fullname) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const labelCounts = new Map<string, number>();
    for (const { label } of others) labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
    return [
        { value: ASSIGNEE_FILTER_UNASSIGNED, label: ASSIGNEE_FILTER_UNASSIGNED },
        ...(currentUserEmail ? [{ value: ASSIGNEE_FILTER_ME, label: ASSIGNEE_FILTER_ME }] : []),
        ...others.map(({ person, label }) => ({
            value: person.email,
            label,
            ...((labelCounts.get(label) ?? 0) > 1 ? { sublabel: person.email } : {}),
            keywords: [person.fullname, person.email]
        }))
    ];
}

const namesFor = (person: AssigneeFilterPerson) => [
    compactName(person.fullname),
    ...legacyAssigneeLabels(person.fullname)
];

// Sentinels and known emails stay; a name label older builds stored becomes the matching
// people's emails; anything else is dropped.
export function migrateAssigneeFilterValues(
    values: readonly string[],
    people: readonly AssigneeFilterPerson[]
): string[] {
    const known = new Set(people.map((person) => person.email));
    const migrated = values.flatMap((value) => {
        if (isSentinel(value) || known.has(value)) return [value];
        return people
            .filter((person) => namesFor(person).includes(value))
            .map((person) => person.email);
    });
    return [...new Set(migrated)];
}

// `unassigned` is the API's own "nobody" token; a value that isn't an email never reaches it.
export function assigneeFilterApiValues(
    values: readonly string[],
    currentUserEmail: string | undefined,
    unassigned: string
): string[] {
    return values.flatMap((value) => {
        if (value === ASSIGNEE_FILTER_UNASSIGNED) return [unassigned];
        if (value === ASSIGNEE_FILTER_ME) return currentUserEmail ? [currentUserEmail] : [];
        return value.includes('@') ? [value] : [];
    });
}

// How the values read (sentinels, then compact names); values naming nobody known are left out.
export function assigneeFilterLabels(
    values: readonly string[],
    people: readonly AssigneeFilterPerson[],
    currentUserEmail: string | undefined
): string[] {
    const nameByEmail = new Map(
        people.map((person) => [person.email, compactName(person.fullname)] as const)
    );
    return values.flatMap((value) => {
        if (value === ASSIGNEE_FILTER_UNASSIGNED) return [value];
        if (value === ASSIGNEE_FILTER_ME) return currentUserEmail ? [value] : [];
        return nameByEmail.get(value) ?? [];
    });
}
